// Live-loop image sizes — deliberately separate from the single-shot constants in ImageFileHelper.ts.
// The live loop uploads ~1 frame/s; 1280px (up from the initial 640px) trades upload size for
// noticeably better localization/OCR on the intermediate frames.
export const AI_LIVE_FRAME_MAX_DIMENSION = 1280;
export const AI_LIVE_FRAME_JPEG_QUALITY = 0.6;
// High-res crop reads ("detect small, read large"): while the live loop keeps sending small
// 640px frames for cheap localization, a crop of the tracked meter box (+padding) is sent at
// native camera resolution for the actual recognition — the AI reads numbers far better from
// a large, meter-filling image than from a downscaled full frame.
export const AI_HIRES_CROP_MAX_DIMENSION = 2560;
export const AI_HIRES_CROP_JPEG_QUALITY = 0.92;
// Delay between hi-res crop reads, measured from the completion of the previous read (the loop is
// self-scheduling, so reads never overlap regardless of this value). 500ms makes the loop
// response-bound: at a fast detect endpoint (~750ms) consensus reads follow ~1.25s apart; when the
// service is slow (2-3s) the wait for the response dominates and this delay is negligible. Also
// used as the recheck delay while the meter is not yet framed for reading.
export const AI_HIRES_CROP_INTERVAL_MS = 500;
// Padding factor around the tracked meter box for the crop (1.2 = 10% margin per side).
export const AI_HIRES_CROP_PADDING_FACTOR = 1.2;
// Minimum on-screen area (relative) of the tracked meter box before high-res crops are sent —
// below this the meter is too small for the crop to contain readable digits anyway.
export const AI_HIRES_MIN_BOX_AREA = 0.08;
// Edge length of the grayscale raster used for the frame-diff gate (motion detection).
export const GRAY_SAMPLE_SIZE = 64;
// Width of the grayscale frame the box tracker operates on. 192px keeps the per-frame cost of
// keypoint matching low while giving the tracker ~1% positional resolution; the previous 96px
// made every 1px match step a 1%-of-image jump and left too little texture for corner selection.
export const TRACKER_FRAME_WIDTH = 192;
// Adaptive fast-detection cadence — the delay until the next box read, measured from the
// completion of the previous one (see helper/detectionPhase.ts). 'acquire' (searching / lost
// lock) polls often to localize; 'track' matches the ~1/s re-anchor cadence useBoxTracker was
// tuned for (the local keypoint tracker holds the box between anchors); 'read' is a heartbeat —
// in the close reading phase the hi-res detect/ loop re-anchors the box itself, so fast only
// needs to keep lastFastBoxRef fresh for the hi-res IoU adopt gate.
export const AI_FAST_DETECT_ACQUIRE_MS = 1000;
export const AI_FAST_DETECT_TRACK_MS = 1500;
export const AI_FAST_DETECT_READ_MS = 1000;
export const AI_FAST_DETECT_TIMEOUT_MS = 2500;
export const AI_FAST_DETECT_MIN_CONFIDENCE = 0.5;

// Evidence-based value consensus (see docs/superpowers/specs/2026-07-08-ai-reading-evidence-consensus-design.md):
// each reading of a value contributes a confidence-weighted "mass"; a value is accepted once the
// summed mass reaches EVIDENCE_REQUIRED_MASS. A single strong (>= EVIDENCE_FULL_WEIGHT_AT) reading
// accepts immediately, while weaker-but-repeated identical readings accumulate toward acceptance.
// Oscillating values (12345 vs 12346) accumulate SEPARATE masses and so never individually reach
// the threshold; only a consistently repeated reading does.
//
// Identical repetition is additionally a signal of its own: EVIDENCE_STREAK_ACCEPT_COUNT
// consecutive identical readings (per task, any other value in between resets the run) accept the
// value regardless of accumulated mass — a wrong-but-confident OCR misread virtually never
// repeats identically several times in a row. Field data (orchestration log 2026-07-08 18:53)
// motivated this: an analog register was read correctly five times at confidences of 0.53–0.87,
// but with the old floor of 0.6 three of those readings counted as exactly zero and acceptance
// took 15s+ of holding still. Hence the floor sits at 0.5 now, and readings at or below
// EVIDENCE_STREAK_MIN_CONFIDENCE are treated as pure noise: they neither contribute mass nor
// count toward/break a streak (a blurry frame between two sharp ones is not a contradiction).
export const EVIDENCE_FLOOR = 0.5;
export const EVIDENCE_FULL_WEIGHT_AT = 0.9;
export const EVIDENCE_EXPONENT = 2;
export const EVIDENCE_REQUIRED_MASS = 1;
export const EVIDENCE_STREAK_ACCEPT_COUNT = 3;
export const EVIDENCE_STREAK_MIN_CONFIDENCE = 0.4;

export const evidenceWeight = (confidence: number): number => {
    const span = EVIDENCE_FULL_WEIGHT_AT - EVIDENCE_FLOOR;
    if (span <= 0) {
        return confidence >= EVIDENCE_FULL_WEIGHT_AT ? 1 : 0;
    }
    const normalized = (confidence - EVIDENCE_FLOOR) / span;
    if (normalized <= 0) {
        return 0;
    }
    if (normalized >= 1) {
        return 1;
    }
    return normalized ** EVIDENCE_EXPONENT;
};

// Minimum IoU between a hi-res crop read's meter box and the last fast-detection box for the
// hi-res box to be adopted as the tracked frame. Below this the crop box is treated as a spurious
// jump and ignored (the fast box keeps the frame); the hi-res result still feeds values/number.
// First calibration — verify on-device.
export const AI_HIRES_BOX_ADOPT_MIN_IOU = 0.6;
